import type { FC, ReactNode } from 'react'
import type { ProductSummary } from '@/types'

import { useRef } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { ProductCard } from '@/components/catalog/product-card'

interface IconsCarouselProps {
    products: Array<ProductSummary>
}

export const IconsCarousel: FC<IconsCarouselProps> = ({ products }): ReactNode => {
    const trackRef = useRef<HTMLUListElement>(null)

    if (products.length === 0) return null

    const scroll = (direction: 1 | -1): void => {
        const track = trackRef.current

        if (!track) return

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        track.scrollBy({ left: direction * track.clientWidth * 0.75, behavior: reduceMotion ? 'auto' : 'smooth' })
    }

    return (
        <section id='icons' aria-labelledby='icons-title' className='border-t border-border py-24 lg:py-36'>
            <div className='mx-auto flex max-w-[1600px] items-end justify-between gap-6 px-6 sm:px-10 lg:px-16'>
                <div className='reveal'>
                    <p className='eyebrow text-muted-foreground'>
                        Selected pieces
                    </p>

                    <h2 id='icons-title' className='mt-4 font-heading text-4xl font-light sm:text-5xl lg:text-6xl'>
                        The Icons
                    </h2>
                </div>

                <div className='flex items-center gap-2'>
                    <button
                        type='button'
                        onClick={() => scroll(-1)}
                        aria-label='Previous pieces'
                        className='flex size-11 items-center justify-center border border-border transition-colors hover:border-foreground'
                    >
                        <ArrowLeft className='size-4' strokeWidth={1.25} aria-hidden />
                    </button>

                    <button
                        type='button'
                        onClick={() => scroll(1)}
                        aria-label='Next pieces'
                        className='flex size-11 items-center justify-center border border-border transition-colors hover:border-foreground'
                    >
                        <ArrowRight className='size-4' strokeWidth={1.25} aria-hidden />
                    </button>
                </div>
            </div>

            <ul
                ref={trackRef}
                className='no-scrollbar mt-12 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-pl-6 px-6 sm:scroll-pl-10 sm:px-10 lg:scroll-pl-[max(4rem,calc((100vw-1600px)/2+4rem))] lg:pl-[max(4rem,calc((100vw-1600px)/2+4rem))] lg:pr-16'
            >
                {products.map(product => (
                    <li key={product.id} className='w-[72vw] shrink-0 snap-start sm:w-[42vw] lg:w-[28vw] xl:w-[22vw] 2xl:w-[340px]'>
                        <ProductCard product={product} sizes='(min-width: 1280px) 22vw, (min-width: 1024px) 28vw, (min-width: 640px) 42vw, 72vw' />
                    </li>
                ))}

                <li aria-hidden className='w-px shrink-0' />
            </ul>
        </section>
    )
}
